import { useEffect, useState } from "react";
import { ChevronLeft, Receipt, Send, XCircle, IndianRupee, FileDown, RotateCcw } from "lucide-react";
import client, { extractError } from "@/api";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const STATUS_LABEL = { DRAFT: "Draft", ISSUED: "Issued", CANCELLED: "Cancelled" };
const FILTERS = [
  { value: "DRAFT", label: "Draft" },
  { value: "ISSUED", label: "Issued" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "", label: "All" },
];

function PaymentDialog({ invoice, onCancel, onConfirm, busy }) {
  const [ref, setRef] = useState("");
  const [method, setMethod] = useState("NEFT");
  const [notes, setNotes] = useState("");
  return (
    <div className="dialog-backdrop" role="dialog" data-testid="payment-dialog">
      <form className="dialog-panel" onSubmit={(e) => { e.preventDefault(); onConfirm({ amount: invoice.total, method, reference: ref, notes: notes || null }); }}>
        <h3>Record payment</h3>
        <p className="muted small">Exact amount ₹{invoice.total.toLocaleString("en-IN")} — partial payments are not supported yet.</p>
        <label>Method<select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="pay-method">
          <option value="NEFT">NEFT</option><option value="RTGS">RTGS</option><option value="UPI">UPI</option>
          <option value="IMPS">IMPS</option><option value="CHEQUE">Cheque</option><option value="CARD">Card</option>
        </select></label>
        <label>Payment reference (bank/UTR)<input required minLength={3} value={ref} onChange={(e) => setRef(e.target.value)} data-testid="pay-reference" /></label>
        <label>Notes (optional)<textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="pay-notes" /></label>
        <div className="dialog-actions">
          <button type="button" className="ghost-button" onClick={onCancel}>Back</button>
          <button type="submit" className="primary-button" disabled={busy} data-testid="pay-confirm">{busy ? "Recording…" : "Record payment"}</button>
        </div>
      </form>
    </div>
  );
}

function CancelDialog({ onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  return (
    <div className="dialog-backdrop" role="dialog" data-testid="cancel-invoice-dialog">
      <form className="dialog-panel" onSubmit={(e) => { e.preventDefault(); onConfirm(reason); }}>
        <h3>Cancel invoice</h3>
        <label>Reason (≥ 5 characters)<textarea required minLength={5} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-invoice-reason" /></label>
        <div className="dialog-actions">
          <button type="button" className="ghost-button" onClick={onCancel}>Back</button>
          <button type="submit" className="danger-button-solid" disabled={busy} data-testid="cancel-invoice-confirm">{busy ? "Working…" : "Confirm cancel"}</button>
        </div>
      </form>
    </div>
  );
}

export default function AdminInvoicesPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("DRAFT");
  const [roleFilter, setRoleFilter] = useState(""); // "", "BUYER", "SELLER"
  const [typeFilter, setTypeFilter] = useState("");
  const [roleByInvoiceId, setRoleByInvoiceId] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [dialog, setDialog] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      if (search) params.set("search", search);
      if (typeFilter) params.set("invoice_type", typeFilter);
      params.set("page_size", 50);
      const [invRes, feeRes] = await Promise.all([
        client.get(`/admin/invoices?${params.toString()}`),
        // Fetch fee records (BUYER + SELLER rows) to map invoice_id → role.
        // Existing endpoint — role is already exposed per fee record.
        client.get("/admin/facilitator-fees?page_size=100"),
      ]);
      setItems(invRes.data.items || []); setCounts(invRes.data.counts || {});
      const map = {};
      for (const f of (feeRes.data.items || [])) {
        if (f.invoice_id) map[f.invoice_id] = f.role;
      }
      setRoleByInvoiceId(map);
    } catch (err) { setError(extractError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, typeFilter]);

  const open = async (id) => {
    try { const r = await client.get(`/admin/invoices/${id}`); setDetail(r.data); setMessage(""); setError(""); }
    catch (err) { setError(extractError(err)); }
  };
  const act = async (path, ok, payload = {}) => {
    if (!detail) return;
    setBusy(true); setError(""); setMessage("");
    try {
      const r = await client.post(`/admin/invoices/${detail.id}/${path}`, payload);
      setDetail((d) => ({ ...d, ...r.data }));
      setMessage(ok); setDialog(null); load();
    } catch (err) { setError(extractError(err)); }
    finally { setBusy(false); }
  };

  if (detail) {
    const detailRole = detail.facilitator_role || roleByInvoiceId[detail.id];
    const isSubscription = detail.invoice_type === "SUBSCRIPTION";
    return (
      <div className="admin-company-detail" data-testid="admin-invoice-detail">
        <button type="button" className="link-button back" onClick={() => setDetail(null)}><ChevronLeft size={14} /> Back to invoices</button>
        <header className="detail-header">
          <div>
            <p className="eyebrow">
              <span data-testid="admin-invoice-detail-type">{isSubscription ? "SUBSCRIPTION" : "FACILITATION"}</span> · {STATUS_LABEL[detail.status] || detail.status}
              {detailRole && (
                <span
                  className={`status-chip status-role-${detailRole.toLowerCase()}`}
                  data-testid="invoice-detail-role"
                  style={{ marginLeft: 8 }}
                >{detailRole}</span>
              )}
            </p>
            <h2>{detail.invoice_number}</h2>
            <p className="muted">To: {detail.recipient_name}{detail.recipient_gstin ? ` · GSTIN ${detail.recipient_gstin}` : ""}</p>
          </div>
          <div className="chip-stack">
            <span className={`status-chip status-${detail.status.toLowerCase()}`}>{STATUS_LABEL[detail.status] || detail.status}</span>
            <button type="button" className="primary-button" data-testid="admin-download-invoice-pdf"
              onClick={() => window.open(`${API}/api/admin/invoices/${detail.id}/pdf`, "_blank")}><FileDown size={13} /> Download PDF</button>
          </div>
        </header>
        {error && <div className="form-error">{error}</div>}
        {message && <div className="form-success" data-testid="admin-invoice-message">{message}</div>}
        <section className="detail-grid">
          <article><p className="eyebrow">{isSubscription ? "SUBSCRIPTION FEE" : "SUBTOTAL (FEE)"}</p><p><strong>₹{detail.subtotal.toLocaleString("en-IN")}</strong></p></article>
          {detail.is_interstate === true ? (
            <article><p className="eyebrow">IGST ({detail.gst_rate_percent}%)</p><p data-testid="admin-invoice-igst">₹{(detail.igst_amount ?? detail.gst_amount).toLocaleString("en-IN")}</p></article>
          ) : detail.is_interstate === false ? (
            <>
              <article><p className="eyebrow">CGST ({detail.gst_rate_percent / 2}%)</p><p data-testid="admin-invoice-cgst">₹{(detail.cgst_amount ?? 0).toLocaleString("en-IN")}</p></article>
              <article><p className="eyebrow">SGST ({detail.gst_rate_percent / 2}%)</p><p data-testid="admin-invoice-sgst">₹{(detail.sgst_amount ?? 0).toLocaleString("en-IN")}</p></article>
            </>
          ) : (
            <article><p className="eyebrow">GST ({detail.gst_rate_percent}%)</p><p data-testid="admin-invoice-gst-legacy">₹{detail.gst_amount.toLocaleString("en-IN")}</p></article>
          )}
          <article><p className="eyebrow">TOTAL</p><p><strong>₹{detail.total.toLocaleString("en-IN")}</strong></p></article>
          <article><p className="eyebrow">REFERENCE</p><p><small>{isSubscription ? detail.line_item_description : detail.transaction?.id?.slice(0, 8) || "—"}</small></p></article>
          <article><p className="eyebrow">ISSUED</p><p>{detail.issued_at ? new Date(detail.issued_at).toLocaleString() : "—"}</p></article>
          <article><p className="eyebrow">DUE</p><p>{detail.due_at ? new Date(detail.due_at).toLocaleString() : "—"}</p></article>
          <article><p className="eyebrow">PDF</p><p data-testid="admin-invoice-pdf-status">{detail.pdf_status || (detail.storage_path ? "GENERATED" : "LEGACY ON-DEMAND")}</p></article>
          <article><p className="eyebrow">EMAIL</p><p data-testid="admin-invoice-email-status">{detail.email_delivery?.status || "NOT QUEUED"}</p></article>
        </section>
        <section className="section-block">
          <p className="eyebrow">ACTIONS</p>
          <div className="button-row">
            {!isSubscription && detail.status === "DRAFT" &&
              <button type="button" className="primary-button" onClick={() => act("issue", "Invoice issued.")} disabled={busy} data-testid="issue-invoice"><Send size={14} /> Issue invoice</button>}
            {!isSubscription && detail.status === "ISSUED" && !detail.paid_at &&
              <button type="button" className="primary-button" onClick={() => setDialog("pay")} disabled={busy} data-testid="record-payment"><IndianRupee size={14} /> Record payment</button>}
            {!isSubscription && ["DRAFT", "ISSUED"].includes(detail.status) && !detail.paid_at &&
              <button type="button" className="danger-button-solid" onClick={() => setDialog("cancel")} disabled={busy} data-testid="cancel-invoice-btn"><XCircle size={14} /> Cancel</button>}
            {detail.pdf_status === "GENERATION_FAILED" &&
              <button type="button" className="secondary-button" onClick={() => act("pdf/retry", "PDF generation retried.")} disabled={busy} data-testid="retry-invoice-pdf"><RotateCcw size={14} /> Retry PDF</button>}
            {["FAILED", "DISABLED"].includes(detail.email_delivery?.status) && detail.pdf_status === "GENERATED" &&
              <button type="button" className="secondary-button" onClick={() => act("email/retry", "Invoice email retry queued.")} disabled={busy} data-testid="retry-invoice-email"><RotateCcw size={14} /> Retry Email</button>}
          </div>
          {detail.generation_error && <p className="form-error small" data-testid="invoice-generation-error">{detail.generation_error}</p>}
          {detail.email_delivery?.error && <p className="form-error small" data-testid="invoice-email-error">{detail.email_delivery.error}</p>}
        </section>
        {detail.payments?.length > 0 && (
          <section className="documents-section">
            <div className="section-heading"><div><p className="eyebrow">PAYMENTS</p><h3>Recorded receipts</h3></div></div>
            <table className="admin-table" data-testid="invoice-payments">
              <thead><tr><th>Amount</th><th>Method</th><th>Reference</th><th>Notes</th><th>When</th></tr></thead>
              <tbody>{detail.payments.map((p) => (
                <tr key={p.id}>
                  <td>₹{p.amount.toLocaleString("en-IN")}</td><td>{p.method}</td><td><small>{p.reference}</small></td>
                  <td><small>{p.notes || "—"}</small></td><td><small>{new Date(p.paid_at).toLocaleString()}</small></td>
                </tr>
              ))}</tbody>
            </table>
          </section>
        )}
        {detail.audit_log?.length > 0 && (
          <section className="documents-section">
            <div className="section-heading"><div><p className="eyebrow">HISTORY</p><h3>Audit trail</h3></div></div>
            <ul className="audit-list" data-testid="admin-invoice-audit">
              {detail.audit_log.map((a) => (
                <li key={a.id}>
                  <strong>{a.event}</strong><small>{a.previous_state || "—"} → {a.new_state || "—"}</small>
                  {a.notes && <p className="muted small">{a.notes}</p>}
                  <small className="muted">{a.actor_email || "system"} · {new Date(a.created_at).toLocaleString()}</small>
                </li>
              ))}
            </ul>
          </section>
        )}
        {dialog === "pay" && <PaymentDialog invoice={detail} onCancel={() => setDialog(null)}
          onConfirm={(payload) => act("payment", "Payment recorded.", payload)} busy={busy} />}
        {dialog === "cancel" && <CancelDialog onCancel={() => setDialog(null)}
          onConfirm={(reason) => act("cancel", "Invoice cancelled.", { reason })} busy={busy} />}
      </div>
    );
  }

  return (
    <div className="admin-listings-page" data-testid="admin-invoices-page">
      <div className="admin-toolbar">
        <div className="filter-tabs" data-testid="admin-invoice-tabs">
          {FILTERS.map((f) => (
            <button key={f.value || "all"} type="button" data-testid={`admin-invoice-filter-${f.value || "all"}`} className={filter === f.value ? "active" : ""} onClick={() => setFilter(f.value)}>
              {f.label}{f.value && counts[f.value] > 0 && <span className="count">{counts[f.value]}</span>}
            </button>
          ))}
        </div>
        <div className="filter-tabs" data-testid="admin-invoice-role-tabs" style={{ marginLeft: 12 }}>
          {[
            { value: "", label: "All roles" },
            { value: "BUYER", label: "Buyer" },
            { value: "SELLER", label: "Seller" },
          ].map((f) => (
            <button
              key={f.value || "all"}
              type="button"
              data-testid={`admin-invoice-role-filter-${f.value || "all"}`}
              className={roleFilter === f.value ? "active" : ""}
              onClick={() => setRoleFilter(f.value)}
            >{f.label}</button>
          ))}
        </div>
        <div className="filter-tabs" data-testid="admin-invoice-type-tabs" style={{ marginLeft: 12 }}>
          {[
            { value: "", label: "All types" },
            { value: "FACILITATION", label: "Facilitation" },
            { value: "SUBSCRIPTION", label: "Subscription" },
          ].map((f) => (
            <button key={f.value || "all"} type="button"
              data-testid={`admin-invoice-type-filter-${f.value || "all"}`}
              className={typeFilter === f.value ? "active" : ""}
              onClick={() => setTypeFilter(f.value)}>{f.label}</button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="search-row">
          <input placeholder="Search invoice # or recipient" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-invoice-search" />
        </form>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        <table className="admin-table" data-testid="admin-invoices-table">
          <thead><tr><th>Invoice #</th><th>Type</th><th>Role</th><th>Recipient</th><th>Subtotal</th><th>GST</th><th>Total</th><th>Status</th><th>Email</th><th></th></tr></thead>
          <tbody>
            {(() => {
              const visible = items.filter((r) => !roleFilter || (r.facilitator_role || roleByInvoiceId[r.id]) === roleFilter);
              if (visible.length === 0) {
                return <tr><td colSpan={10} className="empty-row">No invoices in this view.</td></tr>;
              }
              return visible.map((r) => {
                const role = r.facilitator_role || roleByInvoiceId[r.id];
                return (
                  <tr key={r.id} data-testid={`admin-invoice-row-${r.id}`}>
                    <td><strong>{r.invoice_number}</strong></td>
                    <td><span className="status-chip" data-testid={`admin-invoice-type-${r.id}`}>{r.invoice_type === "SUBSCRIPTION" ? "Subscription" : "Facilitation"}</span></td>
                    <td>
                      {role ? (
                        <span
                          className={`status-chip status-role-${role.toLowerCase()}`}
                          data-testid={`invoice-role-${r.id}`}
                        >{role}</span>
                      ) : <small className="muted">—</small>}
                    </td>
                    <td>{r.recipient_name}<br /><small className="muted">{r.recipient_gstin || "no GSTIN"}</small></td>
                    <td>₹{r.subtotal.toLocaleString("en-IN")}</td>
                    <td>₹{r.gst_amount.toLocaleString("en-IN")}</td>
                    <td><strong>₹{r.total.toLocaleString("en-IN")}</strong></td>
                    <td><span className={`status-chip status-${r.status.toLowerCase()}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                    <td><small data-testid={`admin-invoice-email-${r.id}`}>{r.email_delivery?.status || "—"}</small></td>
                    <td><button type="button" className="link-button" data-testid={`admin-open-invoice-${r.id}`} onClick={() => open(r.id)}><Receipt size={12} /> Review →</button></td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      )}
    </div>
  );
}
